import Error from '../../../model/Error';
import Offering from '../../../dto/offering/Offering';
import axios from 'axios';

export default class CourseOfferingRepository {
    static find(offeringId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/course-offerings/${offeringId}`)
                .then((response) => resolve(Offering.fromData(response.data.data)))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static create(offering, clubId) {
        const offeringData = Object.assign({}, offering);
        offeringData.bookableForAdults = true;
        offeringData.bookableForNonAdults = true;

        if (offering.minimumAge) {
            offeringData.minimumAge = Number.parseInt(offering.minimumAge);
        }
        if (offering.maximumAge) {
            offeringData.maximumAge = Number.parseInt(offering.maximumAge);
        }

        if (offeringData.minimumAge === '') {
            offeringData.minimumAge = null;
        }
        if (offeringData.maximumAge === '') {
            offeringData.maximumAge = null;
        }
        if (offeringData.cancellableUntilAmount === '') {
            offeringData.cancellableUntilAmount = null;
        }

        if (offering.executionType) {
            offeringData.executionType = offering.executionType.value;
        }

        if (offering.sport) {
            offeringData.sportId = offering.sport.id;
            delete offeringData.sport;
        }

        if (offering.fields) {
            offeringData.fieldIds = offering.fields.map((field) => field.id);
            delete offeringData.fields;
        }

        if (offering.departments) {
            offeringData.bookableDepartmentIds = offering.departments.map((department) => department.id);
            delete offeringData.departments;
        }

        if (offering.teams) {
            offeringData.bookableTeamIds = offering.teams.map((team) => team.id);
            delete offeringData.teams;
        }

        if (offering.prices) {
            offeringData.priceIds = offering.prices.map((price) => price.id);
            delete offeringData.prices;
        }

        if (offering.instructors) {
            offeringData.instructorIds = offering.instructors.map((instructor) => instructor.id);
            delete offeringData.instructors;
        }

        if (this.reservationTimeInMinutes && this.reservationTimeInMinutes.value) {
            this.reservationTimeInMinutes = this.reservationTimeInMinutes.value;
        }

        if (this.order && this.order.value) {
            this.order = this.order.value;
        }

        if (offering.salesListOfferingItems) {
            // The picker emits club fees, whose `id` IS the club fee id. A duplicated offering
            // instead carries ClubSalesListOfferingItem objects, whose `id` is the item's own and
            // whose fee sits in `clubFeeId` — sending the former resolves to nothing server-side
            // (ClubFeeListDtoConverter drops what it cannot find) and the items vanish silently.
            offeringData.salesListOfferingItems = offering.salesListOfferingItems.map((item) => item.clubFeeId ?? item.id);
        }

        offeringData.prices = (offering.prices || []).map((price) => {
            return {
                clubFeeId: (price.clubFee && price.clubFee.id ? String(price.clubFee.id) : null),
                departmentId: (price.department ? Number.parseInt(price.department) : null),
                divergentAmount: price.divergentAmount,
                teamId: (price.team ? Number.parseInt(price.team) : null),
                type: (price.type ? String(price.type) : null),
            };
        });

        return new Promise((resolve, reject) => {
            axios
                .post(`/club/clubs/${clubId}/course-offerings`, offeringData)
                .then((response) => resolve(response.headers.location))
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }

    static update(offering) {
        const offeringData = Object.assign({}, offering);

        if (offeringData.minimumAge) {
            offeringData.minimumAge = Number.parseInt(offering.minimumAge);
        }
        if (offeringData.maximumAge) {
            offeringData.maximumAge = Number.parseInt(offering.maximumAge);
        }

        if (offeringData.minimumAge === '') {
            offeringData.minimumAge = null;
        }
        if (offeringData.maximumAge === '') {
            offeringData.maximumAge = null;
        }
        if (offeringData.cancellableUntilAmount === '') {
            offeringData.cancellableUntilAmount = null;
        }

        if (offeringData.executionType) {
            offeringData.executionType = offering.executionType.value;
        }

        offeringData.prices = (offering.prices || []).map((price) => {
            return {
                clubFeeId: (price.clubFee && price.clubFee.id ? String(price.clubFee.id) : null),
                departmentId: (price.department ? Number.parseInt(price.department) : null),
                divergentAmount: price.divergentAmount,
                teamId: (price.team ? Number.parseInt(price.team) : null),
                type: (price.type ? String(price.type) : null),
            };
        });

        if (offeringData.sport) {
            offeringData.sportId = offering.sport.id;
        }
        delete offeringData.sport;

        if (offeringData.fields) {
            offeringData.fieldIds = offering.fields.map((field) => field.id);
        }
        delete offeringData.fields;

        if (offeringData.departments) {
            offeringData.bookableDepartmentIds = offering.departments.map((department) => department.id);
        }
        delete offeringData.departments;

        if (offeringData.teams) {
            offeringData.bookableTeamIds = offering.teams.map((team) => team.id);
        }
        delete offeringData.teams;

        if (offeringData.instructors) {
            offeringData.instructorIds = offering.instructors.map((instructor) => instructor.id);
        }
        delete offeringData.instructors;

        if (this.reservationTimeInMinutes && this.reservationTimeInMinutes.value) {
            this.reservationTimeInMinutes = this.reservationTimeInMinutes.value;
        }

        if (this.order && this.order.value) {
            this.order = this.order.value;
        }

        if (offering.salesListOfferingItems) {
            offeringData.salesListOfferingItems = offering.salesListOfferingItems.map((item) => {
                return {
                    offeringId: offering.id,
                    clubFeeId: item.clubFeeId ?? item.id,
                };
            });
        }

        return new Promise((resolve, reject) => {
            axios
                .put(`/club/course-offerings/${offeringData.id}`, offeringData)
                .then(() => resolve())
                .catch((error) => reject(Error.fromResponse(error.response)));
        });
    }
}
